import * as React from 'react';

import { connect } from 'react-redux';

import { Option } from '@webapp/types/common/option';

import styled, { css } from 'styled-components';
import { getWidth } from '@components/styles';

import AppState from '@webapp/store/state';

import DropdownCtrl from '../dropdown';
import MenuDropdownHeader from './menu-dropdown-header';

import Flex from '@components/layout/flex';
import Tooltip from '@components/basics/tooltip';
import Typography from '@components/basics/typography';
import UnselectedIcon from '@components/basics/icons/unselected';
import CircleCheckIcon from '@components/basics/icons/circle-check';
import { Translate } from 'react-localize-redux';

interface MenuDropdownProps {
  className?: string;
  children?: React.ReactNode;
  menuContent?: Option[];
  label?: React.ReactNode;
  placeholder?: React.ReactNode;
  isSmall?: boolean;
  hasDottedIcon?: boolean;
  mobileTooltip?: boolean;
  active?: any;
  hasMultiselect?: boolean;
  isMobile: boolean;
  isMerchEnabled: boolean;
  onSelect?: (active: any) => void;
  hasWhiteBg?: boolean;
  onClose?: () => void;
}

const mapStateToProps = (state: AppState) => ({
  isMobile: state.ui.mobile,
});

export const MenuDropdown: React.FC<MenuDropdownProps> = (props) => {
  const { onClose } = props;
  const [isOpen, setOpen] = React.useState(false);
  const [isSelected, setSelected] = React.useState(false);

  React.useEffect(() => {
    if (!isOpen) {
      onClose && onClose();
    }
  }, [onClose, isOpen]);

  const handleClick = (item: Option) => {
    return props.onSelect ? props.onSelect(item.value) : null;
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleText = (item: Option, isMobile: boolean) => {
    if (isMobile) {
      return item.withTranslation ? (
        <Typography
          weight="400"
          type="analytics-nav"
          color={item.isRedText ? 'red' : undefined}
        >
          <Translate id={item.label} />
        </Typography>
      ) : item.label === 'Cancel Publishing' ||
        item.label === 'Cancel Invite' ? (
        <Typography weight="400" type="analytics-nav" color="red">
          {item.label}
        </Typography>
      ) : (
        <Typography weight="400" type="analytics-nav">
          {item.label}
        </Typography>
      );
    } else {
      return item.withTranslation ? (
        item.isRedText ? (
          <RedText weight="700">
            <Translate id={item.label} />
          </RedText>
        ) : (
          <Typography weight="700">
            <Translate id={item.label} />
          </Typography>
        )
      ) : item.value.includes('cancel') ||
        item.value.includes('takedown') ||
        item.value.includes('delete') ? (
        <RedText weight="700">{item.label}</RedText>
      ) : (
        <Typography weight="700">
          {props.isMerchEnabled
            ? item.value.includes('subscribe')
              ? item.label.split(' ')[2]
              : item.label
            : item.label}
        </Typography>
      );
    }
  };

  return (
    <DropdownCtrl
      className={props.className}
      customContent
      header={
        <MenuDropdownHeader
          isOpen={isOpen}
          label={props.label}
          placeholder={props.placeholder}
          onClick={() => setOpen(!isOpen)}
          hasDottedIcon={props.hasDottedIcon}
        />
      }
      onClose={handleClose}
    >
      {props.isMobile ? (
        props.menuContent ? (
          props.menuContent.map((item, i) => (
            <MenuDropdownItem
              className={`menu-item ${
                props.active === item.value ? 'selected' : ''
              }`}
              key={`menu-dropdown-item-${i}`}
              onClick={(ev) => {
                ev.stopPropagation();
                handleClick(item);
                setSelected(!isSelected);
              }}
            >
              {props.hasMultiselect ? (
                isSelected ? (
                  <CircleCheckIcon className="menu-dropdown-item-icon" />
                ) : (
                  <UnselectedIcon className="menu-dropdown-item-icon" />
                )
              ) : null}
              {handleText(item, props.isMobile)}
            </MenuDropdownItem>
          ))
        ) : (
          props.children
        )
      ) : isOpen ? (
        <Tooltip
          className="menu-content-wrapper"
          top="0px"
          right="-30px"
          minWidth="auto"
          noMotion
          isOpen={isOpen}
        >
          {props.menuContent
            ? props.menuContent.map((item, i) => (
                <MenuDropdownItem
                  className={`menu-item ${
                    props.active === item.value ? 'selected' : ''
                  }`}
                  key={`menu-dropdown-item-${i}`}
                  onClick={(ev) => {
                    ev.stopPropagation();
                    handleClick(item);
                    setOpen(!isOpen);
                    setSelected(!isSelected);
                  }}
                >
                  {props.hasMultiselect ? (
                    isSelected ? (
                      <CircleCheckIcon className="menu-dropdown-item-icon" />
                    ) : (
                      <UnselectedIcon className="menu-dropdown-item-icon" />
                    )
                  ) : null}
                  {handleText(item, props.isMobile)}
                </MenuDropdownItem>
              ))
            : props.children}
        </Tooltip>
      ) : null}
    </DropdownCtrl>
  );
};

export default connect(mapStateToProps)(styled(MenuDropdown)`
  position: relative;

  .label {
    margin-right: 12px;
  }

  .label,
  .icon-arrow,
  .menu-item {
    cursor: pointer;
    pointer-events: auto;
  }
  ${(props) =>
    props.hasWhiteBg
      ? css`
          .menu-content-wrapper,
          .menu-items-container {
            .menu-content {
              background-color: ${({ theme }) => theme.colors.background};
            }
          }
        `
      : null}
  ${(props) =>
    props.isSmall
      ? css`
          ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
            .menu-content-wrapper,
            .menu-items-container {
              width: ${getWidth(3.5)};
            }
          }
        `
      : css`
          ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
            .menu-content-wrapper,
            .menu-items-container {
              width: max-content;
            }
          }
        `}

  ${(props) =>
    props.mobileTooltip
      ? css`
          .menu-content-wrapper {
            top: 0;
            ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
              top: 100%;
            }
          }
        `
      : null}

  .menu-item {
    opacity: 1;
    transition: opacity
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};

    ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
      margin-bottom: 10px;
    }

    &:last-child {
      margin-bottom: 0;
    }

    &:hover {
      opacity: 0.75;
    }
  }
`);

const MenuDropdownItem = styled(Flex)`
  justify-content: flex-start;
  align-items: center;
  text-align: left;

  .menu-dropdown-item-icon {
    margin-right: 8px;
    height: 24px;
    width: 24px;
  }
`;

const RedText = styled(Typography)`
  color: ${({ theme }) => theme.colors.error};
`;
